import React from 'react';
import { Check } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { pricingTiers } from '@/data/pricing';
import { useScrollReveal } from '@/lib/useScrollReveal';

export const Pricing: React.FC = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-20 bg-surface-secondary" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Start free, scale as you grow. No hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`relative transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${(index + 1) * 100}ms` } as React.CSSProperties}
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <Badge variant="primary">{tier.badge || 'Most Popular'}</Badge>
                </div>
              )}

              <Card
                variant={tier.highlighted ? 'elevated' : 'default'}
                className={`h-full flex flex-col ${
                  tier.highlighted
                    ? 'border-2 border-primary shadow-xl'
                    : 'hover:-translate-y-1'
                }`}
              >
                <div className="p-6 flex-grow">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">{tier.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold font-mono text-text-primary">
                      {tier.price.split('/')[0]}
                    </span>
                    {tier.priceNum > 0 && (
                      <span className="text-text-muted text-sm">/month</span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 pt-0">
                  <Button
                    variant={tier.variant === 'primary' ? 'primary' : 'secondary'}
                    className="w-full"
                  >
                    {tier.cta}
                  </Button>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;